// --- React Import
import React from 'react';
// --- UI Components
import Button from '@dtci/spellbook/Button';
import { Row } from 'antd';
// --- Utility Function
import { usePermissions } from '../../../utils/permissions';

/**
 * ActionButtonsRenderer Component
 *
 * This component is responsible for rendering action buttons in a grid cell.
 * It provides different buttons based on the user's permissions and the data passed to it.
 *
 * @param {Function} onOpenLegalStatusModal - Function to open the legal status modal.
 * @param {Function} onOpenContractualPartiesModal - Function to open the contractual parties modal.
 * @param {Function} onOpenCopyToModal - Function to open the copy to modal.
 * @param {Function} onOpenVersionModal - Function to open the version modal.
 * @param {Function} onOpenDeleteModal - Function to open the delete modal.
 * @param {Function} onOpenCreateModal - Function to open the creation modal.
 * @param {Function} onOpenEditModal - Function to open the edit modal.
 * @param {Function} onOpenViewModal - Function to open the view modal.
 * @returns {Function} - A renderer function that returns the appropriate action buttons based on the user's permissions and data.
 */
export const ProductActionButtonsRenderer = (
    onOpenLegalStatusModal,
    onOpenContractualPartiesModal,
    onOpenCopyToModal,
    onOpenVersionModal,
    onOpenDeleteModal,
    onOpenCreateModal,
    onOpenEditModal,
    onOpenViewModal,
) => {
    const { isLegalUser, isLegalAdmin, isLegalTVAdmin, isProductCommentsAdmin } = usePermissions();

    const Renderer = ({ value, data }) => {
        if (data.hasClearanceMemo) {
            return (
                <ActionButtons
                    value={value}
                    data={data}
                    onOpenLegalStatusModal={onOpenLegalStatusModal}
                    onOpenContractualPartiesModal={onOpenContractualPartiesModal}
                    onOpenCopyToModal={onOpenCopyToModal}
                    onOpenVersionModal={onOpenVersionModal}
                    onOpenDeleteModal={onOpenDeleteModal}
                    onOpenEditModal={onOpenEditModal}
                    onOpenViewModal={onOpenViewModal}
                    isLegalUser={isLegalUser}
                    isLegalAdmin={isLegalAdmin}
                    isLegalTVAdmin={isLegalTVAdmin}
                    isProductCommentsAdmin={isProductCommentsAdmin}
                />
            );
        }

        if (isLegalAdmin) {
            return (
                <Button
                    type="button"
                    styleType="link"
                    className="sb-typography sb-typography_T20 sb-typography_truncate"
                    style={{ paddingRight: 10, color: '#3611c8' }}
                    onClick={onOpenCreateModal(data)}
                >
                    CREATE
                </Button>
            );
        }

        return null;
    };

    return Renderer;
};

/**
 * ActionButtons Component
 *
 * This component renders a set of action buttons based on the user's permissions and the data passed to it.
 * It includes buttons for legal status, edit, view, copy, versions, contractual parties, and delete.
 *
 * @param {Object} props - The props object containing the data and functions for the buttons.
 * @param {string} props.value - The value to be displayed on the legal status button.
 * @param {Object} props.data - The data object containing information about the clearance memo.
 * @param {Function} props.onOpenLegalStatusModal - Function to open the legal status modal.
 * @param {Function} props.onOpenContractualPartiesModal - Function to open the contractual parties modal.
 * @param {Function} props.onOpenCopyToModal - Function to open the copy to modal.
 * @param {Function} props.onOpenVersionModal - Function to open the version modal.
 * @param {Function} props.onOpenDeleteModal - Function to open the delete modal.
 * @param {Function} props.onOpenEditModal - Function to open the edit modal.
 * @param {Function} props.onOpenViewModal - Function to open the view modal.
 * @param {boolean} props.isLegalAdmin - Flag indicating if the user is a legal admin.
 * @param {boolean} props.isLegalTVAdmin - Flag indicating if the user is a legal TV admin.
 * @param {boolean} props.isLegalUser - Flag indicating if the user is a legal user.
 * @param {boolean} props.isProductCommentsAdmin - Flag indicating if the user is a product comments admin.
 * @return {JSX.Element} - The rendered action buttons component.
 */
const ActionButtons = ({
                           value,
                           data,
                           onOpenLegalStatusModal,
                           onOpenContractualPartiesModal,
                           onOpenCopyToModal,
                           onOpenVersionModal,
                           onOpenDeleteModal,
                           onOpenEditModal,
                           onOpenViewModal,
                           isLegalAdmin,
                           isLegalTVAdmin,
                           isLegalUser,
                           isProductCommentsAdmin,
                       }) => (
    <>
        {/* Row containing action buttons */}
        <Row>
            <div className="btn-group">
                {(isLegalAdmin || isLegalUser) && (
                    <Button
                        style={{
                            paddingRight: 10,
                            color: isLegalAdmin ? '#3611c8' : 'inherit',
                            cursor: 'pointer',
                        }}
                        type="button"
                        styleType="link"
                        className="sb-typography sb-typography_T20 sb-typography_truncate"
                        onClick={onOpenLegalStatusModal(data)}
                        disabled={!isLegalAdmin}
                    >
                        {value}
                    </Button>
                )}

                {(isLegalAdmin) && (
                    <Button
                        style={{ paddingRight: 10, color: '#3611c8' }}
                        type="button"
                        styleType="link"
                        className="sb-typography sb-typography_T20 sb-typography_truncate"
                        onClick={onOpenEditModal(data)}
                    >
                        EDIT
                    </Button>
                )}

                {(isLegalAdmin || isLegalUser) &&
                    ![2, 7].includes(data.legalStatusId) && (
                        <Button
                            style={{ paddingRight: 10, color: '#3611c8' }}
                            type="button"
                            styleType="link"
                            className="sb-typography sb-typography_T20 sb-typography_truncate"
                            onClick={onOpenViewModal(data)}
                        >
                            VIEW
                        </Button>
                    )}

                {isLegalAdmin && (
                    <Button
                        style={{ paddingRight: 10, color: '#3611c8' }}
                        type="button"
                        styleType="link"
                        className="sb-typography sb-typography_T20 sb-typography_truncate"
                        onClick={onOpenCopyToModal(data)}
                    >
                        COPY
                    </Button>
                )}

                {isLegalAdmin && (
                    <Button
                        style={{ paddingRight: 10, color: '#3611c8' }}
                        type="button"
                        styleType="link"
                        className="sb-typography sb-typography_T20 sb-typography_truncate"
                        onClick={onOpenVersionModal(data)}
                    >
                        VERSIONS
                    </Button>
                )}
            </div>
        </Row>

        <Row>
            <div className="btn-group">
                {isLegalAdmin && (
                    <>
                        <Button
                            style={{ paddingRight: 10, color: '#3611c8' }}
                            type="button"
                            styleType="link"
                            className="sb-typography sb-typography_T20 sb-typography_truncate"
                            onClick={onOpenContractualPartiesModal(data)}
                        >
                            CONTRACTUAL PARTIES
                        </Button>

                        <Button
                            style={{ paddingRight: 10, color: '#3611c8' }}
                            type="button"
                            styleType="link"
                            className="sb-typography sb-typography_T20 sb-typography_truncate"
                            onClick={onOpenDeleteModal(data)}
                        >
                            DELETE
                        </Button>
                    </>
                )}
            </div>
        </Row>
    </>
);
